import * as Sentry from "@sentry/react";
import { CameraProvider } from "components/Camera/Camera";
import useMaintenance from "hooks/useMaintenance";
import { createRef, lazy, Suspense } from "react";
import { Route, Routes } from "react-router";
import { useLocation } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useDefaultLanguage } from "./useDefaultLanguage";
import { useRedirectHomeOnIdle } from "./useRedirectHomeOnIdle";
import { useRefreshWhenStale } from "./useRefreshWhenStale";
import RouteFallback from "./RouteFallback";
import InventoryFlowLayout from "components/InventoryFlowLayout";
import InventoryFlowRedirect from "components/InventoryFlowRedirect";
import InventoryRentalRouteGuard from "./InventoryRentalRouteGuard";

const AddRecipient = lazy(() => import("pages/AddRecipient/AddRecipient"));
const CancelReservation = lazy(() => import("pages/CancelReservation/CancelReservation"));
const CarrierValidation = lazy(() => import("pages/CarrierValidation/CarrierValidation"));
const CodeProgress = lazy(() => import("pages/CodeProgress/CodeProgress"));
const CodeSuccess = lazy(() => import("pages/CodeSuccess/CodeSuccess"));
const DepositForm = lazy(() => import("pages/DepositForm/DepositForm"));
const DepositSuccess = lazy(() => import("pages/DepositSuccess/DepositSuccess"));
const Help = lazy(() => import("pages/Help/Help"));
const Home = lazy(() => import("pages/Home/Home"));
const InvalidCode = lazy(() => import("pages/InvalidCode/InvalidCode"));
const Maintenance = lazy(() => import("pages/Maintenance/Maintenance"));
const RecipientForm = lazy(() => import("pages/RecipientForm/RecipientForm"));
const RecipientPassword = lazy(() => import("pages/RecipientsPassword/RecipientsPassword"));
const RecipientsConfirmation = lazy(
  () => import("pages/RecipientsConfirmation/RecipientsConfirmation")
);
const ReservationPayment = lazy(() => import("pages/ReservationPayment/ReservationPayment"));
const Services = lazy(() => import("pages/Services/Services"));
const SelectDoor = lazy(() => import("pages/SelectDoor/SelectDoor"));
const SelectItem = lazy(() => import("pages/SelectItem/SelectItem"));
const SelectRecipient = lazy(() => import("pages/SelectRecipient/SelectRecipient"));
const Test = lazy(() => import("pages/Test/Test"));
const WithdrawConfirm = lazy(() => import("pages/WithdrawConfirm/WithdrawConfirm"));
const WithdrawSuccess = lazy(() => import("pages/WithdrawSuccess/WithdrawSuccess"));
const EquipmentRental = lazy(() => import("pages/EquipmentRental/EquipmentRental"));
const EquipmentDetail = lazy(() => import("pages/EquipmentDetail/EquipmentDetail"));
const EquipmentConfirmation = lazy(
  () => import("pages/EquipmentConfirmation/EquipmentConfirmation")
);
const EquipmentComplete = lazy(() => import("pages/EquipmentComplete/EquipmentComplete"));
const InventoryReturn = lazy(() => import("pages/InventoryReturn/InventoryReturn"));
const InventorySupplyConfirmation = lazy(
  () => import("pages/InventorySupplyConfirmation/InventorySupplyConfirmation")
);

export default function Router() {
  const { maintenance, offline } = useMaintenance();
  const nodeRef = createRef<any>();
  const location = useLocation();

  useDefaultLanguage();
  useRedirectHomeOnIdle();
  useRefreshWhenStale();
  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  if (maintenance) return <Maintenance />;

  const withInventoryRentalGuard = (element: JSX.Element) => (
    <InventoryRentalRouteGuard>{element}</InventoryRentalRouteGuard>
  );

  return (
    <CameraProvider>
      <TransitionGroup>
        <CSSTransition nodeRef={nodeRef} key={location.key} classNames="fade" timeout={250}>
          <div ref={nodeRef}>
            <SentryRoutes location={location}>
              <Route
                path="/cancel-reservation"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <CancelReservation />
                  </Suspense>
                }
              />
              <Route
                path="/recipients-password"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <RecipientPassword />
                  </Suspense>
                }
              />
              <Route
                path="/recipient-form"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <RecipientForm />
                  </Suspense>
                }
              />
              <Route
                path="/code-progress"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <CodeProgress />
                  </Suspense>
                }
              />
              <Route
                path="/code-success"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <CodeSuccess />
                  </Suspense>
                }
              />
              <Route
                path="/deposit-success"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <DepositSuccess />
                  </Suspense>
                }
              />
              <Route
                path="/reservation-payment/:reservationId"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <ReservationPayment />
                  </Suspense>
                }
              />
              <Route
                path="/withdraw-success"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <WithdrawSuccess />
                  </Suspense>
                }
              />
              <Route
                path="/withdraw-confirm"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <WithdrawConfirm />
                  </Suspense>
                }
              />
              <Route
                path="/select-door"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <SelectDoor />
                  </Suspense>
                }
              />
              <Route
                path="/recipients-confirmation"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <RecipientsConfirmation />
                  </Suspense>
                }
              />
              <Route
                path="/add-recipient"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <AddRecipient />
                  </Suspense>
                }
              />
              <Route
                path="/deposit-form"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <DepositForm />
                  </Suspense>
                }
              />
              <Route
                path="/select-recipient"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <SelectRecipient />
                  </Suspense>
                }
              />
              <Route
                path="/invalid-code"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <InvalidCode />
                  </Suspense>
                }
              />
              <Route
                path="/carrier-validation"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <CarrierValidation />
                  </Suspense>
                }
              />
              <Route
                path="/services"
                element={
                  withInventoryRentalGuard(
                    <Suspense fallback={<RouteFallback />}>
                      <Services />
                    </Suspense>
                  )
                }
              />
              <Route path="/inventory" element={<InventoryFlowLayout />}>
                <Route
                  path="rental"
                  element={
                    withInventoryRentalGuard(
                      <Suspense fallback={<RouteFallback />}>
                        <EquipmentRental />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path="select"
                  element={
                    withInventoryRentalGuard(
                      <Suspense fallback={<RouteFallback />}>
                        <SelectItem />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path="item"
                  element={
                    withInventoryRentalGuard(
                      <Suspense fallback={<RouteFallback />}>
                        <EquipmentDetail />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path="confirm"
                  element={
                    withInventoryRentalGuard(
                      <Suspense fallback={<RouteFallback />}>
                        <EquipmentConfirmation />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path="complete"
                  element={
                    withInventoryRentalGuard(
                      <Suspense fallback={<RouteFallback />}>
                        <EquipmentComplete />
                      </Suspense>
                    )
                  }
                />
                <Route
                  path="return"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InventoryReturn />
                    </Suspense>
                  }
                />
                <Route
                  path="supply"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InventorySupplyConfirmation />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="/equipment-rental"
                element={
                  withInventoryRentalGuard(
                    <InventoryFlowRedirect to="/inventory/rental" mode="rental" step="identify" />
                  )
                }
              />
              <Route
                path="/equipment-confirmation"
                element={
                  withInventoryRentalGuard(
                    <InventoryFlowRedirect to="/inventory/confirm" mode="rental" step="confirm" />
                  )
                }
              />
              <Route
                path="/equipment-detail"
                element={withInventoryRentalGuard(
                  <InventoryFlowRedirect to="/inventory/item" mode="rental" step="detail" />
                )}
              />
              <Route
                path="/equipment-complete"
                element={
                  withInventoryRentalGuard(
                    <InventoryFlowRedirect to="/inventory/complete" mode="rental" step="complete" />
                  )
                }
              />
              <Route
                path="/inventory-return"
                element={
                  <InventoryFlowRedirect to="/inventory/return" mode="return" step="return" />
                }
              />
              <Route
                path="/inventory-supply-confirmation"
                element={
                  <InventoryFlowRedirect to="/inventory/supply" mode="supply" step="supply" />
                }
              />
              <Route
                path="/select-item"
                element={
                  withInventoryRentalGuard(
                    <InventoryFlowRedirect to="/inventory/select" mode="rental" step="select" />
                  )
                }
              />
              <Route
                path="/help"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <Help />
                  </Suspense>
                }
              />
              <Route
                path="/Test"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <Test />
                  </Suspense>
                }
              />
              <Route
                path="/"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <Home offline={offline} />
                  </Suspense>
                }
              />
            </SentryRoutes>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </CameraProvider>
  );
}
